import { useState } from "react";
import { usePhysicalProducts } from "hooks/use-physical-products";
import { PhysicalProduct, CreateProductRequest, UpdateProductRequest } from "types/physical-product";
import { Modal } from "BEDROCK/shared-components/modal";
import { AlertModal } from "BEDROCK/shared-components/alert-modal";
import { CTAButton } from "BEDROCK/shared-components/cta-button";
import styles from "./admin-products-page.module.css";

interface ProductFormData {
  sku: string;
  size_description: string;
  material_description: string;
  width_mm: string;
  height_mm: string;
  price_int: string;
  order: string;
  enabled: boolean;
}

const emptyFormData: ProductFormData = {
  sku: "",
  size_description: "",
  material_description: "",
  width_mm: "",
  height_mm: "",
  price_int: "",
  order: "0",
  enabled: false,
};

export const AdminProductsPage = () => {
  const {
    products,
    isLoading,
    isError,
    refetch,
    createProduct,
    isCreating,
    updateProduct,
    isUpdating,
    deleteProduct,
    isDeleting,
  } = usePhysicalProducts();

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<PhysicalProduct | null>(null);
  const [formData, setFormData] = useState<ProductFormData>(emptyFormData);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteConfirmProduct, setDeleteConfirmProduct] = useState<PhysicalProduct | null>(null);

  const handleOpenAddModal = () => {
    setEditingProduct(null);
    setFormData(emptyFormData);
    setFormError(null);
    setIsFormModalOpen(true);
  };

  const handleOpenEditModal = (product: PhysicalProduct) => {
    setEditingProduct(product);
    setFormData({
      sku: product.sku,
      size_description: product.size_description,
      material_description: product.material_description || "",
      width_mm: product.width_mm.toString(),
      height_mm: product.height_mm.toString(),
      price_int: product.price_int.toString(),
      order: product.order.toString(),
      enabled: product.enabled,
    });
    setFormError(null);
    setIsFormModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setIsFormModalOpen(false);
    setEditingProduct(null);
    setFormData(emptyFormData);
    setFormError(null);
  };

  const handleFormChange = (field: keyof ProductFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = (): boolean => {
    if (!formData.sku.trim()) {
      setFormError("SKU is required");
      return false;
    }
    if (!formData.size_description.trim()) {
      setFormError("Size description is required");
      return false;
    }
    if (!formData.width_mm || isNaN(Number(formData.width_mm)) || Number(formData.width_mm) <= 0) {
      setFormError("Valid width is required");
      return false;
    }
    if (!formData.height_mm || isNaN(Number(formData.height_mm)) || Number(formData.height_mm) <= 0) {
      setFormError("Valid height is required");
      return false;
    }
    if (!formData.price_int || isNaN(Number(formData.price_int)) || Number(formData.price_int) <= 0) {
      setFormError("Valid price is required");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setFormError(null);

    try {
      if (editingProduct) {
        const updates: UpdateProductRequest = {
          id: editingProduct.id,
        };
        if (formData.sku !== editingProduct.sku) updates.sku = formData.sku;
        if (formData.size_description !== editingProduct.size_description) updates.size_description = formData.size_description;
        if (formData.material_description !== (editingProduct.material_description || "")) updates.material_description = formData.material_description;
        if (Number(formData.width_mm) !== editingProduct.width_mm) updates.width_mm = Number(formData.width_mm);
        if (Number(formData.height_mm) !== editingProduct.height_mm) updates.height_mm = Number(formData.height_mm);
        if (Number(formData.price_int) !== editingProduct.price_int) updates.price_int = Number(formData.price_int);
        if (Number(formData.order) !== editingProduct.order) updates.order = Number(formData.order);
        if (formData.enabled !== editingProduct.enabled) updates.enabled = formData.enabled;

        await updateProduct(updates);
      } else {
        const newProduct: CreateProductRequest = {
          sku: formData.sku,
          size_description: formData.size_description,
          material_description: formData.material_description || undefined,
          width_mm: Number(formData.width_mm),
          height_mm: Number(formData.height_mm),
          price_int: Number(formData.price_int),
          order: Number(formData.order) || 0,
          enabled: formData.enabled,
        };
        await createProduct(newProduct);
      }
      handleCloseFormModal();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleDeleteClick = (product: PhysicalProduct) => {
    setDeleteConfirmProduct(product);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmProduct) return;
    try {
      await deleteProduct(deleteConfirmProduct.id);
      setDeleteConfirmProduct(null);
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const formatPrice = (priceInt: number): string => {
    return `£${(priceInt / 100).toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading products...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          Error loading products. Please try again.
          <button onClick={() => refetch()} className={styles.retryButton}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Products</h1>
        <div className={styles.controls}>
          <button onClick={() => refetch()} className={styles.refreshButton}>
            Refresh
          </button>
          <CTAButton onClick={handleOpenAddModal}>
            Add Product
          </CTAButton>
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Order</th>
              <th>SKU</th>
              <th>Size</th>
              <th>Material</th>
              <th>Dimensions (mm)</th>
              <th>Price</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products?.map((product) => (
              <tr key={product.id}>
                <td>{product.order}</td>
                <td className={styles.skuCell}>{product.sku}</td>
                <td>{product.size_description}</td>
                <td>{product.material_description || "-"}</td>
                <td>{product.width_mm} x {product.height_mm}</td>
                <td className={styles.priceCell}>{formatPrice(product.price_int)}</td>
                <td>
                  <span className={product.enabled ? styles.statusEnabled : styles.statusDisabled}>
                    {product.enabled ? "Enabled" : "Disabled"}
                  </span>
                </td>
                <td className={styles.actionsCell}>
                  <button
                    className={styles.actionButton}
                    onClick={() => handleOpenEditModal(product)}
                  >
                    Edit
                  </button>
                  <button
                    className={`${styles.actionButton} ${styles.deleteButton}`}
                    onClick={() => handleDeleteClick(product)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {(!products || products.length === 0) && (
              <tr>
                <td colSpan={8} className={styles.emptyState}>
                  No products found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={isFormModalOpen} onClose={handleCloseFormModal}>
        <div className={styles.formModal}>
          <h2>{editingProduct ? "Edit Product" : "Add Product"}</h2>

          {formError && <div className={styles.formError}>{formError}</div>}

          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="sku">SKU *</label>
              <input
                id="sku"
                type="text"
                value={formData.sku}
                onChange={(e) => handleFormChange("sku", e.target.value)}
                placeholder="e.g., DIBOND-A2"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="size_description">Size Description *</label>
              <input
                id="size_description"
                type="text"
                value={formData.size_description}
                onChange={(e) => handleFormChange("size_description", e.target.value)}
                placeholder="e.g., A2 (420 x 594mm)"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="material_description">Material Description</label>
              <input
                id="material_description"
                type="text"
                value={formData.material_description}
                onChange={(e) => handleFormChange("material_description", e.target.value)}
                placeholder="e.g., Aluminum Dibond"
              />
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="width_mm">Width (mm) *</label>
                <input
                  id="width_mm"
                  type="number"
                  value={formData.width_mm}
                  onChange={(e) => handleFormChange("width_mm", e.target.value)}
                  placeholder="420"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="height_mm">Height (mm) *</label>
                <input
                  id="height_mm"
                  type="number"
                  value={formData.height_mm}
                  onChange={(e) => handleFormChange("height_mm", e.target.value)}
                  placeholder="594"
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="price_int">Price (pence) *</label>
                <input
                  id="price_int"
                  type="number"
                  value={formData.price_int}
                  onChange={(e) => handleFormChange("price_int", e.target.value)}
                  placeholder="7500"
                />
                {formData.price_int && (
                  <span className={styles.pricePreview}>
                    = {formatPrice(Number(formData.price_int))}
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="order">Display Order</label>
                <input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => handleFormChange("order", e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>

            <div className={styles.checkboxGroup}>
              <label htmlFor="enabled" className={styles.checkboxLabel}>
                <input
                  id="enabled"
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => handleFormChange("enabled", e.target.checked)}
                />
                <span>Enabled</span>
              </label>
              <span className={styles.checkboxHint}>
                Disabled products will not be shown to customers
              </span>
            </div>
          </div>

          <div className={styles.formActions}>
            <CTAButton variant="SECONDARY" onClick={handleCloseFormModal}>
              Cancel
            </CTAButton>
            <CTAButton
              onClick={handleSubmit}
              disabled={isCreating || isUpdating}
            >
              {isCreating || isUpdating ? "Saving..." : editingProduct ? "Update" : "Create"}
            </CTAButton>
          </div>
        </div>
      </Modal>

      <AlertModal
        isShowing={!!deleteConfirmProduct}
        title="Delete Product"
        message={`Are you sure you want to delete "${deleteConfirmProduct?.sku}"? This will archive the Stripe price and remove the product from the database.`}
        proceedCTA={isDeleting ? "Deleting..." : "Delete"}
        dismissCTA="Cancel"
        onProceed={handleConfirmDelete}
        onDismiss={() => setDeleteConfirmProduct(null)}
      />
    </div>
  );
};
